import { Button, Input } from '@lokalise/louis'
import type { Integrations } from '@lokalise/translation-api-schemas'
import { useState } from 'react'
import { requiredErrorMessage } from '../../../../constants/errors'
import { AI_PROFILES_DOCUMENTATION_URL } from '../../../../constants/url'
import { AiModelSelect } from '../../../AiModelSelect/AiModelSelect'
import { CustomTargetLanguagesView } from '../../../KeysForLanguage/components/CustomTargetLanguagesView/CustomTargetLanguagesView'
import {
  KeysForLanguageBorderWrapper,
  KeysForLanguageContainer,
  KeysForLanguageContents,
  KeysForLanguageHeader,
  KeysForLanguagesSourceArrow,
  KeysForLanguagesSourceCard,
  KeysForLanguagesTargetLanguagesContainer,
} from '../../../KeysForLanguage/KeysForLanguage'
import {
  StepContainer,
  StepContents,
  StepFooter,
  StepRSP,
  StepSubtitle,
  StepTitle,
  StepTitleContainer,
} from '../../../Step/Step'
import { StyledLink } from '../../../StyledLink/StyledLink'
import styles from './ProfileBasics.module.css'

type ProfileBasicsStepProps = {
  name: string
  isEdit: boolean
  selectedAiModel: Integrations
  onAiModelSelected: (aiModel: Integrations) => void
  onNameChange: (name: string) => void
  onNext: () => void
  onClose: () => void
}

export const ProfileBasicsStep = ({
  onNext,
  onClose,
  name,
  onNameChange,
  selectedAiModel,
  onAiModelSelected,
}: ProfileBasicsStepProps) => {
  const [hasClickedNext, setHasClickedNext] = useState(false)
  const nameError = hasClickedNext && !name.length && requiredErrorMessage

  const onClickNext = () => {
    setHasClickedNext(true)

    if (name.length && onNext) {
      onNext()
    }
  }

  return (
    <StepContainer>
      <StepContents>
        <StepTitleContainer>
          <StepTitle>Choose your profile basics</StepTitle>
          <StepSubtitle>
            Choose a name that reflects the profile's purpose. The LLM provider is set to routing by
            default, as it ensures the best translation quality.{' '}
            <StyledLink href={AI_PROFILES_DOCUMENTATION_URL} rel="noreferrer" target="_blank">
              Docs.
            </StyledLink>
          </StepSubtitle>
        </StepTitleContainer>
        <div className="flex-col gap-5">
          <div className="flex-col gap-2">
            <span className={styles.heading} id="name-input">
              Write profile name
            </span>
            <Input
              autoFocus
              aria-labelledby="name-input"
              error={nameError}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="Profile name"
              value={name}
            />
          </div>
          <AiModelSelect selectedModel={selectedAiModel} onModelChange={onAiModelSelected} />
        </div>
        <StepFooter>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onClickNext} variant="primary">
            Next
          </Button>
        </StepFooter>
      </StepContents>
      <StepRSP>
        <KeysForLanguageContainer>
          <KeysForLanguageBorderWrapper>
            <KeysForLanguageHeader name={name} integration={selectedAiModel} />
            <KeysForLanguageContents>
              <KeysForLanguagesSourceCard />

              <KeysForLanguagesSourceArrow />

              <KeysForLanguagesTargetLanguagesContainer>
                <CustomTargetLanguagesView />
              </KeysForLanguagesTargetLanguagesContainer>
            </KeysForLanguageContents>
          </KeysForLanguageBorderWrapper>
        </KeysForLanguageContainer>
      </StepRSP>
    </StepContainer>
  )
}
